import { useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { TERMS_DOCUMENT_ID, TERMS_VERSION } from "@/lib/terms-metadata";
import { recordTermsAcceptance } from "@/lib/record-terms-acceptance";

interface TermsAcceptanceModalProps {
  isOpen: boolean;
  userId: string;
  onAccepted: () => void;
}

export const TermsAcceptanceModal = ({
  isOpen,
  userId,
  onAccepted,
}: TermsAcceptanceModalProps) => {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleAccept = async () => {
    if (!acceptedTerms) {
      toast({
        title: "Aceite os termos para continuar",
        description: "Você precisa aceitar os Termos de Uso e a Política de Privacidade.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await recordTermsAcceptance(userId, "modal");

      if (error) {
        console.error("Error saving terms acceptance:", error);
        toast({
          title: "Erro ao salvar",
          description: "Não foi possível salvar sua aceitação. Tente novamente.",
          variant: "destructive",
        });
        return;
      }

      onAccepted();
    } catch (err) {
      console.error("Unexpected error:", err);
      toast({
        title: "Erro inesperado",
        description: "Ocorreu um erro. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Contrato de Uso da Plataforma</AlertDialogTitle>
          <AlertDialogDescription className="text-left space-y-3">
            <p>
              Para continuar usando o FabChat, você precisa aceitar nosso contrato eletrônico de prestação de serviços.
            </p>
            <p className="text-sm">
              O aceite produz os mesmos efeitos de um contrato assinado, incluindo as regras de planos, cobrança,
              compromisso anual parcelado (quando aplicável) e direito de arrependimento de 7 dias previsto no CDC.
            </p>
            <p className="text-xs text-muted-foreground">
              Documento: {TERMS_DOCUMENT_ID} · versão {TERMS_VERSION}
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-start space-x-3">
            <Checkbox
              id="accept-terms"
              checked={acceptedTerms}
              onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
              disabled={isLoading}
            />
            <Label
              htmlFor="accept-terms"
              className="text-sm leading-relaxed cursor-pointer"
            >
              Li, compreendi e <strong>aceito eletronicamente</strong> os{" "}
              <Link
                to="/termos"
                target="_blank"
                className="text-primary hover:underline font-medium"
              >
                Termos de Serviço
              </Link>{" "}
              (versão {TERMS_VERSION}), incluindo as condições de assinatura e cobrança, e a{" "}
              <Link
                to="/privacidade"
                target="_blank"
                className="text-primary hover:underline font-medium"
              >
                Política de Privacidade
              </Link>
              . Declaro possuir poderes para contratar em nome da conta utilizada.
            </Label>
          </div>
        </div>

        <AlertDialogFooter>
          <Button
            onClick={handleAccept}
            disabled={!acceptedTerms || isLoading}
            className="w-full"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              "Aceitar Contrato e Continuar"
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
